'use client';

import { useSyncExternalStore } from 'react';
import Script from 'next/script';
import { usePathname } from 'next/navigation';

// Loads GA4 with auto page_view enabled. Gated on NEXT_PUBLIC_GA_ID so the
// tag is absent locally and on any deploy without the env var set.
// Staff (admins + operators) are excluded three ways:
//   1. Server-resolved isStaff from the current session (covers logged-in staff)
//   2. ffl_no_track cookie (set on staff login, survives logout for ~2y)
//   3. ffl_no_track localStorage flag (set by visiting /no-track on any device)
// /admin/* is also excluded regardless of session.
//
// Loaded with lazyOnload so the gtag bundle (~150 KiB) does not compete with
// LCP. Attribution does not depend on GA4 - first-touch is captured server-side
// via /api/attribution/track from <AttributionCapture />, so a missed GA4
// page_view on a fast bouncer has no business impact.

const GA_ID = process.env.NEXT_PUBLIC_GA_ID;
const NO_TRACK_KEY = 'ffl_no_track';

function hasDeviceOptOut(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.cookie.split('; ').some((c) => c.startsWith(`${NO_TRACK_KEY}=1`))) return true;
  try {
    if (window.localStorage.getItem(NO_TRACK_KEY) === '1') return true;
  } catch {
    // localStorage may be blocked (private mode, strict ITP); fall through.
  }
  return false;
}

function subscribe() {
  return () => {};
}

export function GA4({ isStaff = false }: { isStaff?: boolean }) {
  const pathname = usePathname();
  const optedOut = useSyncExternalStore(
    subscribe,
    hasDeviceOptOut,
    () => false,
  );

  if (!GA_ID) return null;
  if (isStaff) return null;
  if (optedOut) return null;
  if (pathname?.startsWith('/admin')) return null;
  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
        strategy="lazyOnload"
      />
      <Script id="ga4-init" strategy="lazyOnload">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', '${GA_ID}', { anonymize_ip: true });
        `}
      </Script>
    </>
  );
}
